import { CheckCheck, CircleAlert, Clock, Eye, Send } from "lucide-react";
import { StatCard } from "@/components/ui/stat-card";
import { ChartCard } from "@/components/ui/chart-card";
import { OrgDeliveryKpis } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import { META_ERROR_LABELS, formatRatio } from "../org-kpis.meta";
import { BreakdownBars } from "./BreakdownBars";

interface DeliveryTabProps {
  delivery: OrgDeliveryKpis;
}

export function DeliveryTab({ delivery }: DeliveryTabProps) {
  const unconfirmed = delivery.sent + delivery.pending;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Sent to WhatsApp"
          value={formatters.formatNumber(delivery.attempted)}
          icon={Send}
        />
        <StatCard
          label="Delivered"
          value={formatRatio(delivery.deliveryRate)}
          hint={`${formatters.formatNumber(delivery.delivered + delivery.read)} messages`}
          icon={CheckCheck}
          tone="primary"
        />
        <StatCard
          label="Read"
          value={formatRatio(delivery.readRate)}
          hint="Of delivered messages"
          icon={Eye}
        />
        <StatCard
          label="Failed"
          value={formatRatio(delivery.failureRate)}
          hint={`${formatters.formatNumber(delivery.failed)} messages`}
          icon={CircleAlert}
          tone={delivery.failureRate !== null && delivery.failureRate > 0.05 ? "danger" : "default"}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard
          title="Why messages failed"
          description="Meta's error codes, most common first. Failed messages are not charged."
        >
          <BreakdownBars
            rows={delivery.topErrors.map((e) => ({
              label:
                e.code === null
                  ? "No error code"
                  : `${e.code} · ${META_ERROR_LABELS[e.code] ?? "Other Meta error"}`,
              value: e.count
            }))}
            format={formatters.formatNumber}
            emptyText="No failed messages in this period."
          />
        </ChartCard>
        <ChartCard title="Waiting for a receipt">
          <div className="flex items-start gap-3 text-sm">
            <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              {formatters.formatNumber(unconfirmed)} messages were accepted by Meta but have no
              delivered or read receipt yet. A high share over several days usually means status
              webhooks are not reaching the platform.
            </p>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
